/**
 * Ícone e TOM de cada tipo de notificação — a fonte única.
 *
 * Existiam DUAS listas: uma na antiga tabela da página (18 tipos) e outra em
 * `SidebarNotificationPanel.vue` (11 tipos). Além de divergirem em tamanho,
 * a do sino tinha três chaves que o backend NUNCA emite —
 * `assigned_conversation_creation`, `sla_breach` e `followup_due` não estão
 * em `Notification::NOTIFICATION_TYPES`. Ou seja: três ícones escritos que
 * nunca apareceram, e uma porção de tipos reais caindo no sino genérico.
 *
 * Aqui estão os 28 tipos do enum do backend, conferidos um a um contra
 * `app/models/notification.rb`. Quando alguém acrescentar um tipo lá, o
 * fallback continua sendo o sino cinza — mas agora há UM lugar para
 * corrigir, não dois.
 *
 * O TOM não é decoração: é o que deixa a lista varrível sem ler. Cada família
 * de assunto tem a sua cor no produto, a mesma do resto do sistema:
 *
 *   azul     conversa e atendimento     iris     jurídico
 *   teal     funil (pipeline)           âmbar    financeiro e prazo
 *   violeta  chat interno               rubi     risco, perda, atraso
 *   slate    automação e sistema
 */

const TIPO = (icone, tom) => ({ icone, tom });

export const TIPOS_DE_NOTIFICACAO = {
  // ── Conversa e atendimento ────────────────────────────────────────────
  conversation_creation: TIPO('i-lucide-message-circle', 'azul'),
  conversation_assignment: TIPO('i-lucide-user-check', 'azul'),
  assigned_conversation_new_message: TIPO('i-lucide-message-square', 'azul'),
  conversation_mention: TIPO('i-lucide-at-sign', 'azul'),
  participating_conversation_new_message: TIPO(
    'i-lucide-messages-square',
    'azul'
  ),

  // ── SLA: prazo de atendimento estourado ───────────────────────────────
  sla_missed_first_response: TIPO('i-lucide-timer-off', 'rubi'),
  sla_missed_next_response: TIPO('i-lucide-timer-off', 'rubi'),
  sla_missed_resolution: TIPO('i-lucide-timer-off', 'rubi'),

  // ── Lead ──────────────────────────────────────────────────────────────
  lead_score_category_change: TIPO('i-lucide-trending-up', 'teal'),
  lead_became_hot: TIPO('i-lucide-flame', 'ambar'),
  bulk_recalculate_completed: TIPO('i-lucide-check-check', 'slate'),

  // ── Funil ─────────────────────────────────────────────────────────────
  pipeline_card_assigned: TIPO('i-lucide-user-plus', 'teal'),
  pipeline_card_moved: TIPO('i-lucide-arrow-right-left', 'teal'),
  pipeline_card_won: TIPO('i-lucide-trophy', 'teal'),
  pipeline_card_lost: TIPO('i-lucide-circle-x', 'rubi'),
  pipeline_sla_exceeded: TIPO('i-lucide-timer-off', 'rubi'),

  // ── Jurídico ──────────────────────────────────────────────────────────
  legal_process_new_movement: TIPO('i-lucide-scale', 'iris'),
  legal_new_publication: TIPO('i-lucide-newspaper', 'iris'),
  legal_activity_reminder: TIPO('i-lucide-calendar-clock', 'iris'),
  legal_fatal_deadline: TIPO('i-lucide-siren', 'rubi'),

  // ── Automação ─────────────────────────────────────────────────────────
  automation_notification: TIPO('i-lucide-bot', 'slate'),

  // ── Chat interno ──────────────────────────────────────────────────────
  internal_chat_mention: TIPO('i-lucide-at-sign', 'violeta'),
  internal_chat_dm_message: TIPO('i-lucide-message-square', 'violeta'),
  internal_chat_message: TIPO('i-lucide-messages-square', 'violeta'),

  // ── Financeiro ────────────────────────────────────────────────────────
  financial_obligation_overdue: TIPO('i-lucide-circle-dollar-sign', 'ambar'),

  // ── Cadência de follow-up ─────────────────────────────────────────────
  // Três destas são ESCALADA: algo saiu do trilho e alguém precisa olhar.
  follow_up_cadence_circuit_breaker: TIPO('i-lucide-power-off', 'ambar'),
  follow_up_cadence_crisis_detected: TIPO('i-lucide-siren', 'rubi'),
  follow_up_cadence_generation_failed: TIPO('i-lucide-triangle-alert', 'rubi'),
  follow_up_cadence_silence_escalated: TIPO('i-lucide-volume-off', 'ambar'),

  // ── Segurança ─────────────────────────────────────────────────────────
  lumos_safety_risk_detected: TIPO('i-lucide-shield-alert', 'rubi'),
};

const RESERVA = TIPO('i-lucide-bell', 'slate');

export const iconeDoTipo = tipo =>
  (TIPOS_DE_NOTIFICACAO[tipo] || RESERVA).icone;

export const tomDoTipo = tipo => (TIPOS_DE_NOTIFICACAO[tipo] || RESERVA).tom;
